import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAtomValue, getDefaultStore } from 'jotai';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { isAuthenticatedAtom, isRehydratingAtom, authUserMetaAtom } from '@/store/authAtoms';
import { selectedFacilityAtom } from '@/store/facilityAtom';
import { remoteAccessContextAtom } from '@/store/remoteAccessContextAtom';
import { useSessionCaps } from '@/hooks/useSessionCaps';

const store = getDefaultStore();

export default function ProtectedRoute() {
  const isRehydrating = useAtomValue(isRehydratingAtom);
  const isAuth = useAtomValue(isAuthenticatedAtom);
  const remoteAccessCtx = useAtomValue(remoteAccessContextAtom);
  const facility = useAtomValue(selectedFacilityAtom);
  const { isICodeSession, isGuestSession, caps, canAccess } = useSessionCaps();
  const location = useLocation();
  const { pathname, search, hash } = location;

  // Last-resort guard: if something outside our control clears authUserMetaAtom while
  // a remote access session is still active, restore it so the page doesn't redirect.
  // Under normal operation this should never fire — clearAuthAndRedirect() returns early
  // for remote access sessions before touching any auth state.
  useEffect(() => {
    if (!isAuth && remoteAccessCtx !== null) {
      console.warn('[ProtectedRoute] authUserMetaAtom cleared during remote access session — restoring from context.');
      store.set(authUserMetaAtom, {
        id:       String(remoteAccessCtx.personKey),
        fullName: `Patient ${remoteAccessCtx.personKey}`,
        roles:    ['remoteAccess'],
      });
    }
  }, [isAuth, remoteAccessCtx]);

  // While the bootstrap silent refresh is in-flight, show a spinner rather than
  // redirecting — the token hasn't been set yet but the cookie may still be valid.
  if (isRehydrating) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuth && remoteAccessCtx !== null) {
    // Auth transiently null during remote access — the useEffect above restores it on
    // the next tick. Keep rendering <Outlet /> so the page tree stays mounted and
    // in-progress form state is not lost.
    return <Outlet />;
  }

  if (!isAuth) {
    // Root path: send to the appropriate pre-auth entry point.
    //   No facility → /facility-selection  (first-time or cleared selection)
    //   Has facility → /home               (landing hub for sign-in / other options)
    // Deep link (any other path): skip the hub when a facility is already selected
    // and go straight to /sign-in so the user can authenticate with state.from intact.
    let authEntry: string;
    if (pathname === '/') {
      authEntry = facility ? '/home' : '/facility-selection';
    } else {
      authEntry = facility ? '/sign-in' : '/home';
    }
    return <Navigate to={authEntry} replace state={{ from: { pathname, search, hash } }} />;
  }

  // iCode sessions are restricted to the routes declared in their capability map.
  if (isICodeSession && caps && !canAccess(pathname)) {
    return <Navigate to={caps.landingRoute} replace />;
  }

  // Guest sessions can only reach /appointments and /help.
  if (isGuestSession && !canAccess(pathname)) {
    return <Navigate to="/appointments" replace />;
  }

  return <Outlet />;
}
